import type { IconWeight, Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Button as AriaButton,
  composeRenderProps,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { cn } from '../lib/cn';
import { Icon } from './Icon';

/*
 * Hover styles use React Aria's data-hovered, which is only set for real pointers.
 * On touch devices there is no "sticky hover" after a tap.
 */
export const buttonStyles = cva(
  [
    'inline-flex items-center justify-center gap-gap-sm select-none',
    'font-body font-semibold rounded-control cursor-pointer',
    'transition duration-(--shh-motion-duration-quick) ease-standard',
    // Sinks into its shadow in the kid block look; a slight scale in the flat look.
    'data-pressed:scale-97 data-pressed:translate-x-(--shh-card-press-offset) data-pressed:translate-y-(--shh-card-press-offset)',
    'outline-none data-focus-visible:outline-3 data-focus-visible:outline-offset-2 data-focus-visible:outline-border-focus',
    'data-disabled:cursor-not-allowed data-disabled:bg-action-disabled data-disabled:text-action-on-disabled data-disabled:shadow-none',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-action-primary text-text-on-action shadow-card data-pressed:shadow-none data-hovered:bg-action-primary-hover data-pressed:bg-action-primary-pressed',
        secondary:
          'bg-action-secondary text-action-on-secondary data-hovered:bg-action-secondary-hover data-pressed:bg-action-secondary-pressed',
        quiet:
          'bg-transparent text-text-primary data-hovered:bg-action-quiet-hover data-pressed:bg-action-secondary-hover',
        danger:
          'bg-feedback-danger-surface text-feedback-danger data-hovered:bg-action-secondary-hover data-pressed:bg-action-secondary-pressed',
      },
      size: {
        md: 'min-h-touch-min min-w-touch-min px-inset-md text-md',
        lg: 'min-h-touch-target min-w-touch-target px-inset-lg text-lg',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends Omit<AriaButtonProps, 'className'>, VariantProps<typeof buttonStyles> {
  /** Leading icon. */
  icon?: PhosphorIcon;
  className?: string;
}

export function Button({
  variant,
  size,
  fullWidth,
  icon,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <AriaButton {...props} className={cn(buttonStyles({ variant, size, fullWidth }), className)}>
      {composeRenderProps(children, (content) => (
        <>
          {icon && <Icon icon={icon} size={size === 'lg' ? 'lg' : 'md'} />}
          {content}
        </>
      ))}
    </AriaButton>
  );
}

export interface IconButtonProps
  extends
    Omit<AriaButtonProps, 'className' | 'children'>,
    Omit<VariantProps<typeof buttonStyles>, 'fullWidth'> {
  icon: PhosphorIcon;
  /** Symbol weight – e.g. 'bold' for simple glyphs like plus or close. */
  iconWeight?: IconWeight;
  /** Required: an icon-only button needs an accessible name. */
  'aria-label': string;
  className?: string;
}

export function IconButton({
  variant = 'quiet',
  size,
  icon,
  iconWeight,
  className,
  ...props
}: IconButtonProps) {
  return (
    <AriaButton
      {...props}
      className={cn(buttonStyles({ variant, size }), 'px-0 aspect-square', className)}
    >
      <Icon icon={icon} weight={iconWeight} size={size === 'lg' ? 'lg' : 'md'} />
    </AriaButton>
  );
}
